import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './LandingP.css';
import CountdownTimer from './CountdownTimer';
import FallingDots from './FallingDots';
import { dbData } from './data';
import { StarIcon, ShieldCheckIcon, SparklesIcon, CheckIcon, ChevronDownIcon, CartIcon } from './Icons';

const VARIANTS = [
    { id: 1, name: 'Blue', color: '#1a56db', productKey: 'blue', arName: 'أزرق' },
    { id: 3, name: 'Red', color: '#e53935', productKey: 'red', arName: 'أحمر' },
];

function StarRating({ rating = 4.8, count = 543 }) {
    const { t } = useTranslation();
    return (
        <div className="star-rating">
            <div className="stars">
                {[1, 2, 3, 4, 5].map(s => (
                    <span key={s} className={`star ${s <= Math.floor(rating) ? 'filled' : s <= rating ? 'half' : ''}`}>
                        <StarIcon />
                    </span>
                ))}
            </div>
            <span className="rating-value">{rating}</span>
            <span className="rating-count">({count.toLocaleString()} {t('common.reviews')})</span>
        </div>
    );
}

function FeatureTag({ icon, text }) {
    return (
        <div className="feature-tag">
            <span className="feature-icon">{icon}</span>
            <span>{text}</span>
        </div>
    );
}

function LandingP({ setPage, activeKey, setActiveKey }) {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language === 'ar' ? 'ar' : (i18n.language === 'fr' ? 'fr' : 'en');

    const [allProducts, setAllProducts] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [imgVisible, setImgVisible] = useState(false);
    const [swipeDirection, setSwipeDirection] = useState('next'); // 'next' or 'prev'
    const imgRef = useRef(null);
    const touchStartX = useRef(null);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleMouseDown = (e) => {
        touchStartX.current = e.clientX;
    };




    const handleEnd = (clientX) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - clientX;
        const threshold = 50; // Minimum swipe distance

        if (Math.abs(diff) > threshold) {
            const currentIndex = VARIANTS.findIndex(v => v.productKey === activeKey);
            if (diff > 0) {
                // Swipe Left -> Next
                const nextIndex = (currentIndex + 1) % VARIANTS.length;
                handleVariantChange(VARIANTS[nextIndex].productKey);
            } else {
                // Swipe Right -> Prev
                const prevIndex = (currentIndex - 1 + VARIANTS.length) % VARIANTS.length;
                handleVariantChange(VARIANTS[prevIndex].productKey);
            }
        }
        touchStartX.current = null;
    };

    const handleTouchEnd = (e) => {
        handleEnd(e.changedTouches[0].clientX);
    };

    const handleMouseUp = (e) => {
        handleEnd(e.clientX);
    };

    useEffect(() => {
        setAllProducts(dbData.products);
        setTimeout(() => setIsLoaded(true), 100);
    }, []);

    const handleVariantChange = (newKey) => {
        if (newKey === activeKey) return;

        const currentIndex = VARIANTS.findIndex(v => v.productKey === activeKey);
        const nextIndex = VARIANTS.findIndex(v => v.productKey === newKey);

        setSwipeDirection(nextIndex > currentIndex ? 'next' : 'prev');
        setImgVisible(false);

        setTimeout(() => {
            setActiveKey(newKey);
        }, 300);
    };

    useEffect(() => {
        const t = setTimeout(() => setImgVisible(true), 80);
        return () => clearTimeout(t);
    }, [activeKey]);

    if (!allProducts) return (
        <div className="landing-loading">
            <div className="loading-ring"></div>
            <p>{t('landing.loading')}</p>
        </div>
    );

    const currentProduct = allProducts[activeKey];
    const content = currentProduct[currentLang] || currentProduct['en'];

    return (
        <div className={`landing-container ${isLoaded ? 'loaded' : ''}`}>
            <div className="hero-section">
                {/* ── Left: text ─────────────────────────── */}
                <div className="hero-text" style={{ display: 'flex', flexDirection: 'column' }}>
                    {(currentLang === 'ar' || currentLang === 'fr') && (
                        <div className="brand-badge">
                            <span className="badge-dot"></span>
                            {content.type} · {content.brand}
                        </div>
                    )}

                    <div className={`text-content-animate ${imgVisible ? 'visible' : 'exit'} ${swipeDirection}`}>
                        <StarRating />

                        <div style={{ margin: '15px 0' }}>
                            <CountdownTimer targetDate={new Date(Date.now() + 1000 * 60 * 60 * 24 * 2)} />
                        </div>

                        <div className="price-container">
                            <span className="price">{currentProduct.price} <small>{t('common.mad')}</small></span>
                            <span className="old-price">{currentProduct.oldPrice} {t('common.mad')}</span>
                            <span className="discount-badge">
                                -{Math.round((1 - currentProduct.price / currentProduct.oldPrice) * 100)}%
                            </span>
                            <span className="size-label">{content.size_ml}ml</span>
                        </div>


                        {/* CTA */}
                        <div className="cta-section">
                            <button className="details-button main shine-button" onClick={() => setPage('details')}>
                                <SparklesIcon /> {t('common.viewDetails')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Center: Hanging Buy Button ──────────────── */}
                <div className="hanging-buy-container">
                    <div className="hanging-line"></div>
                    <div className="hanging-btn-wrapper">
                        <button className="hanging-buy-btn shine-button" onClick={() => setPage('buy')}>
                            <CartIcon /> {t('common.buyNow')}
                        </button>
                    </div>
                </div>

                {/* ── Right: product image + color selector ── */}
                <div
                    className="hero-image"
                    style={{ cursor: 'grab' }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => touchStartX.current = null}
                >
                    <div className="image-stage">
                        <FallingDots color={VARIANTS.find(v => v.productKey === activeKey)?.color || '#fff'} />
                        <div className="glow-ring"></div>
                        <div className="product-placeholder-glow"></div>
                        <div className="perspective-container">
                            <img
                                src={currentProduct.image}
                                alt={content.name}
                                className={`glow-product-img ${imgVisible ? 'visible' : 'exit'} ${swipeDirection}`}
                            />
                        </div>

                    </div>

                    {/* Color selector */}
                    <div className="color-selector">
                        {VARIANTS.map(v => (
                            <button
                                key={v.id}
                                className={`color-circle ${activeKey === v.productKey ? 'active' : ''}`}
                                style={{ backgroundColor: v.color }}
                                onClick={() => handleVariantChange(v.productKey)}
                                title={i18n.language === 'ar' ? (v.arName || v.name) : v.name}
                            >
                                {activeKey === v.productKey && <span className="circle-check"><CheckIcon /></span>}
                            </button>
                        ))}
                    </div>
                    <p className="variant-name">
                        {t(`common.variants.${activeKey}`)}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default LandingP;
